/** Image URLs served by our backend are resized once and cached in MinIO. */

const CDN_BASE = "https://wsrv.nl/?url=";

interface ImageOptions {
  width?: number;
  height?: number;
  quality?: number;
  fit?: "contain" | "cover" | "fill" | "inside" | "outside";
}

type FirstPartyPreset = "thumbnail" | "card" | "medium" | "full";

/**
 * Normalize URL - decode first to handle already-encoded URLs, then encode properly
 */
const normalizeUrl = (url: string): string => {
  try {
    // Decode first to normalize (handles %20 -> space), then encode properly
    return encodeURI(decodeURI(url.trim()));
  } catch {
    // If decoding fails, try encoding directly
    return encodeURI(url.trim());
  }
};

const getFirstPartyVariantUrl = (originalUrl: string, preset: FirstPartyPreset): string | null => {
  try {
    const parsed = new URL(normalizeUrl(originalUrl));
    const marker = "/files/";
    const markerIndex = parsed.pathname.indexOf(marker);
    if (!/^https?:$/.test(parsed.protocol) || markerIndex < 0) return null;
    const rawKey = decodeURIComponent(parsed.pathname.slice(markerIndex + marker.length));
    const encodedKey = rawKey.split("/").map(encodeURIComponent).join("/");
    return `${parsed.origin}/image/variant/${preset}/${encodedKey}`;
  } catch {
    return null;
  }
};

/**
 * Get optimized image URL via CDN with resizing
 */
export const getOptimizedImageUrl = (
  originalUrl: string,
  options: ImageOptions = {},
  firstPartyPreset?: FirstPartyPreset,
): string => {
  // Skip optimization for local/placeholder images
  if (!originalUrl || originalUrl.startsWith("/") || originalUrl.startsWith("data:")) {
    return originalUrl;
  }

  if (firstPartyPreset) {
    const firstPartyUrl = getFirstPartyVariantUrl(originalUrl, firstPartyPreset);
    if (firstPartyUrl) return firstPartyUrl;
  }

  // Normalize the URL first to handle both encoded and non-encoded URLs
  const normalizedUrl = normalizeUrl(originalUrl);

  const { width = 400, height, quality = 80, fit = "cover" } = options;

  const params = new URLSearchParams();
  params.set("w", width.toString());
  if (height) params.set("h", height.toString());
  params.set("q", quality.toString());
  params.set("fit", fit);
  params.set("output", "webp"); // Use WebP for better compression

  return `${CDN_BASE}${encodeURIComponent(normalizedUrl)}&${params.toString()}`;
};

/**
 * Get thumbnail URL for product cards and lists (small, fast-loading)
 */
export const getThumbnailUrl = (originalUrl: string): string => {
  return getOptimizedImageUrl(originalUrl, {
    width: 480,
    height: 480,
    quality: 75,
    fit: "cover",
  }, "card");
};

/**
 * Get medium-sized image for carousels and hero sections
 */
export const getMediumImageUrl = (originalUrl: string): string => {
  return getOptimizedImageUrl(originalUrl, {
    width: 900,
    height: 900,
    quality: 80,
    fit: "inside",
  }, "medium");
};

/**
 * Get full resolution image for detail views (higher quality but still optimized)
 */
export const getFullImageUrl = (originalUrl: string): string => {
  return getOptimizedImageUrl(originalUrl, {
    width: 1600,
    quality: 90,
    fit: "contain",
  }, "full");
};
